import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

const tagVariants = cva(
  "inline-flex max-w-full shrink-0 items-center border font-medium leading-none shadow-none",
  {
    variants: {
      tone: {
        custom: "",
        neutral:
          "border-border bg-muted/70 text-muted-foreground dark:bg-muted/40",
        brand:
          "border-primary/20 bg-primary/10 text-primary dark:border-primary/30 dark:bg-primary/15",
        info: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200",
        success:
          "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
        warning:
          "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200",
        danger: "border-destructive/25 bg-destructive/10 text-destructive",
      },
      size: {
        compact: "h-5 gap-1 px-2 text-xs",
        default: "h-6 gap-1.5 px-2.5 text-xs",
      },
      shape: {
        rounded: "rounded-md",
        pill: "rounded-full",
      },
    },
    defaultVariants: {
      tone: "neutral",
      size: "default",
      shape: "rounded",
    },
  }
);

export type TagTone = NonNullable<VariantProps<typeof tagVariants>["tone"]>;
export type TagSize = NonNullable<VariantProps<typeof tagVariants>["size"]>;

export type TagAppearance = {
  /** 背景色・枠線・文字色をひとまとまりで渡す。 */
  containerClassName?: string;
  /** ドットを表示する場合の色。 */
  indicatorClassName?: string;
  /** テナント設定など、実行時に決まる任意色を渡す。 */
  containerStyle?: React.CSSProperties;
  /** 実行時に決まるドット色を渡す。 */
  indicatorStyle?: React.CSSProperties;
};

const indicatorClasses: Record<Exclude<TagTone, "custom">, string> = {
  neutral: "bg-muted-foreground/60",
  brand: "bg-primary",
  info: "bg-sky-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-destructive",
};

export interface TagProps
  extends React.ComponentPropsWithoutRef<"span">,
    VariantProps<typeof tagVariants> {
  icon?: LucideIcon;
  indicator?: boolean;
  appearance?: TagAppearance;
  indicatorClassName?: string;
}

/**
 * 一覧・詳細・フィルターで使う短いラベルの共通土台。
 * 通常ラベルはneutral/brand、状態表示はStatusBadge、分類はCategoryTagを使う。
 * custom・shape・appearanceは業務ラッパーを作る場合だけ使用する。
 * 色は文字と併用し、色だけで状態や分類を伝えない。
 */
export function Tag({
  tone = "neutral",
  size,
  shape,
  icon: Icon,
  indicator = false,
  appearance,
  indicatorClassName,
  className,
  style,
  children,
  ...props
}: TagProps) {
  const resolvedTone = tone ?? "neutral";
  const defaultIndicatorClass =
    resolvedTone === "custom" ? "bg-current" : indicatorClasses[resolvedTone];

  return (
    <span
      data-slot="tag"
      data-tone={resolvedTone}
      className={cn(
        tagVariants({ tone: resolvedTone, size, shape }),
        appearance?.containerClassName,
        className
      )}
      style={{ ...appearance?.containerStyle, ...style }}
      {...props}
    >
      {Icon ? <Icon aria-hidden="true" className="size-3.5 shrink-0" /> : null}
      {!Icon && indicator ? (
        <span
          aria-hidden="true"
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            defaultIndicatorClass,
            appearance?.indicatorClassName,
            indicatorClassName
          )}
          style={appearance?.indicatorStyle}
        />
      ) : null}
      {children}
    </span>
  );
}

export { tagVariants };
